import type { IWorkJourneyMatrixCell } from '@src/models/interfaces/workJourneyManagement.interface';

import { isFutureMatrixCalendarDate } from './isFutureMatrixCalendarDate';
import {
  resolveMatrixRemoveExceptionCopyKey,
  type MatrixRemoveExceptionCopyKey,
} from './isRemovableMatrixExceptionCell';

type RemovableSelectionCell = Pick<
  IWorkJourneyMatrixCell,
  'date' | 'exceptionId' | 'kind' | 'code' | 'exceptionType'
>;

export type RemovableMatrixExceptionSelection<T extends RemovableSelectionCell> = {
  cell: T;
  copyKey: MatrixRemoveExceptionCopyKey;
};

/**
 * Resolves whether the current matrix selection may offer "remove exception".
 * Absences stay single-day from the bar; replacements may span several days of
 * the same exception (typical cover-only REM selection).
 */
export const resolveRemovableMatrixExceptionSelection = <T extends RemovableSelectionCell>(
  cells: Array<T | null | undefined>,
  options?: { nowMs?: number },
): RemovableMatrixExceptionSelection<T> | null => {
  const resolved = cells.filter((cell): cell is T => cell != null);
  if (resolved.length === 0 || resolved.length !== cells.length) return null;
  if (resolved.some(cell => !isFutureMatrixCalendarDate(cell.date, options?.nowMs))) {
    return null;
  }

  const first = resolved[0];
  const copyKey = resolveMatrixRemoveExceptionCopyKey(first);
  if (!copyKey) return null;

  const sameException = resolved.every(
    cell =>
      cell.exceptionId === first.exceptionId &&
      resolveMatrixRemoveExceptionCopyKey(cell) === copyKey,
  );
  if (!sameException) return null;

  if (copyKey !== 'replacement' && resolved.length !== 1) return null;

  return { cell: first, copyKey };
};
